import { create } from 'zustand';
import { 
  signInWithEmailAndPassword, 
  createUserWithEmailAndPassword,
  signOut as firebaseSignOut,
  onAuthStateChanged,
  updateProfile,
  User 
} from 'firebase/auth';
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { initializeApp, getApps } from 'firebase/app';
import { getAuth } from 'firebase/auth';
import { getFirestore } from 'firebase/firestore';

// Firebase config
const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY || 'demo-api-key',
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN || 'lawity-gl-2025.firebaseapp.com',
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID || 'lawity-gl-2025',
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET || 'lawity-gl-2025.appspot.com',
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID || '123456789',
  appId: import.meta.env.VITE_FIREBASE_APP_ID || '1:123456789:web:abc123',
};

// Initialize Firebase
const app = getApps().length === 0 ? initializeApp(firebaseConfig) : getApps()[0];
const auth = getAuth(app);
const db = getFirestore(app);

export interface ClientProfile {
  uid: string;
  email: string;
  displayName: string;
  phone?: string;
  address?: {
    street1: string;
    city: string;
    state: string;
    zipCode: string;
  };
  createdAt: Date;
}

interface ClientAuthState {
  user: User | null;
  profile: ClientProfile | null;
  loading: boolean;
  error: string | null;
  initialized: boolean;
  
  initialize: () => void;
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (email: string, password: string, displayName: string) => Promise<void>;
  signOut: () => Promise<void>;
  updateProfile: (data: Partial<ClientProfile>) => Promise<void>;
  clearError: () => void;
}

export const useClientAuth = create<ClientAuthState>((set, get) => ({
  user: null,
  profile: null,
  loading: false,
  error: null,
  initialized: false,

  initialize: () => {
    if (get().initialized) return;
    
    onAuthStateChanged(auth, async (user) => {
      if (user) {
        try {
          const profileDoc = await getDoc(doc(db, 'clients', user.uid));
          if (profileDoc.exists()) {
            const data = profileDoc.data();
            set({
              user,
              profile: {
                uid: user.uid,
                email: user.email || '',
                displayName: data.displayName || user.displayName || '',
                phone: data.phone,
                address: data.address,
                createdAt: data.createdAt?.toDate() || new Date(),
              },
              initialized: true,
            });
          } else {
            set({ user, profile: null, initialized: true });
          }
        } catch (err) {
          console.error('Error fetching profile:', err);
          set({ user, profile: null, initialized: true });
        }
      } else {
        set({ user: null, profile: null, initialized: true });
      }
    });
  },

  signIn: async (email: string, password: string) => {
    set({ loading: true, error: null });
    try {
      await signInWithEmailAndPassword(auth, email, password);
      set({ loading: false });
    } catch (err: any) {
      let message = 'Failed to sign in';
      switch (err.code) {
        case 'auth/invalid-email':
          message = 'Invalid email address';
          break;
        case 'auth/user-not-found':
          message = 'No account found with this email';
          break;
        case 'auth/wrong-password':
          message = 'Incorrect password';
          break;
        case 'auth/too-many-requests':
          message = 'Too many attempts. Please try again later';
          break;
      }
      set({ loading: false, error: message });
      throw new Error(message);
    }
  },

  signUp: async (email: string, password: string, displayName: string) => {
    set({ loading: true, error: null });
    try {
      const { user } = await createUserWithEmailAndPassword(auth, email, password);
      
      // Update display name
      await updateProfile(user, { displayName });
      
      // Create client profile in Firestore
      await setDoc(doc(db, 'clients', user.uid), {
        uid: user.uid,
        email,
        displayName,
        createdAt: serverTimestamp(),
      });

      set({
        loading: false,
        profile: {
          uid: user.uid,
          email,
          displayName,
          createdAt: new Date(),
        },
      });
    } catch (err: any) {
      let message = 'Failed to create account';
      switch (err.code) {
        case 'auth/email-already-in-use':
          message = 'An account with this email already exists';
          break;
        case 'auth/weak-password':
          message = 'Password must be at least 6 characters';
          break;
        case 'auth/invalid-email':
          message = 'Invalid email address';
          break;
      }
      set({ loading: false, error: message });
      throw new Error(message);
    }
  },

  signOut: async () => {
    set({ loading: true });
    try {
      await firebaseSignOut(auth);
      set({ user: null, profile: null, loading: false });
    } catch (err) {
      set({ loading: false, error: 'Failed to sign out' });
      throw err;
    }
  },

  updateProfile: async (data: Partial<ClientProfile>) => {
    const { user } = get();
    if (!user) throw new Error('Not signed in');

    set({ loading: true, error: null });
    try {
      await setDoc(doc(db, 'clients', user.uid), data, { merge: true });
      set(state => ({
        loading: false,
        profile: state.profile ? { ...state.profile, ...data } : null,
      }));
    } catch (err) {
      set({ loading: false, error: 'Failed to update profile' });
      throw err;
    }
  },

  clearError: () => set({ error: null }),
}));

// Initialize auth on module load
useClientAuth.getState().initialize();
